import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as uuid from 'uuid';
import useResizeObserver from 'use-resize-observer';

import {
  EnginePage,
  Main,
  OfferingPage,
  ReportsPage,
  RuleCreatePage,
  RuleEditPage,
  RulePage,
  RulesPage,
  SettingsPage,
  SideMenu,
  SidePanel,
  SpinCentered,
  UserCreatePage,
  UserEditPage,
  ValueDashboardPage,
  ReportsTabPage,
} from '@/comp';
import { permissions } from '@/constants';
import { useStores } from '@/hooks/use-stores';
import MobileDrawerMenu from '@/comp/pages/StationPage/MobileDrawerMenu/MobileDrawerMenu';
import { useIsMobile } from '@/hooks/useIsMobile';

import CentrifugalPage from '../EnginePage/Engine/CentrifugalCompressors/CentrifugalPage';
import LifecyclePage from '../LifecyclePage';
import { StationPageContainer } from './StationPage.styles';
import InstallationValidationPage from '../InstallerPage/InstallationValidationPage';
import AIAssistantPage from '../AIAssistantPage/AIAssistantPage';
import SiteSurveyOverlay from '@/comp/SiteSurvey/SiteSurveyOverlay';
import { NavigateWithParams } from './StationPage.utils';
import { LifecycleStageId } from '../LifecyclePage/constants';

const minResponsiveWidth = 1280;

const StationPage = observer(() => {
  const { stationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { stationStore, permissionStore, siteSurveyStore } = useStores();
  const isReady = getIsReady();
  const bodyRef = useRef(document.body);

  // Replace ResizeObserver with hook-based approach
  const { width } = useResizeObserver({ ref: bodyRef });
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { isMobile } = useIsMobile();

  const [showSiteSurvey, setShowSiteSurvey] = useState(false);
  const [salesforceId, setSalesforceId] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  // Update windowWidth when width changes
  useEffect(() => {
    if (width) {
      setWindowWidth(width);
    }
  }, [width]);

  useEffect(() => {
    // Get salesforceId from search params
    const sfId = searchParams.get('salesforceId');
    if (sfId) {
      setSalesforceId(sfId);
    }

    // Get salesforceRegion from search params and set it to store
    const sfRegion = searchParams.get('salesforceRegion');
    if (sfRegion) {
      siteSurveyStore.setSelectedRegion(sfRegion.toUpperCase());
    }

    const surveyParam = searchParams.get('showSiteSurvey');
    if (surveyParam?.toLocaleLowerCase() === 'true') {
      setShowSiteSurvey(true);
    }
    if (!siteSurveyStore?.isUserEligibleToForm && (sfId || surveyParam)) {
      handleCloseSurvey();
    }
  }, [searchParams]);

  const handleCloseSurvey = () => {
    setShowSiteSurvey(false);
    setSalesforceId(null);
    siteSurveyStore.setSelectedRegion(null);

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('showSiteSurvey');
    newSearchParams.delete('salesforceId');
    newSearchParams.delete('salesforceRegion');
    navigate({
      pathname: window.location.pathname,
      search: newSearchParams.toString() ? `?${newSearchParams.toString()}` : '',
    });
  };

  useEffect(() => {
    stationStore.setUseMockData(window.location.pathname.includes('/tab'));
  }, [window.location.pathname]);

  // Redirect away from report-tab when switching to a Monitor station
  useEffect(() => {
    const isOnReportTab = location.pathname.includes('/report-tab');
    if (
      isOnReportTab &&
      stationStore.isMonitorStationTierForPeriodicReports &&
      [
        LifecycleStageId.Subscription,
        LifecycleStageId.Baseline,
        LifecycleStageId.ControlPrep,
        LifecycleStageId.Deprecated,
      ].includes(stationStore.currentStationStage?.stage?.id)
    ) {
      const newSearchParams = new URLSearchParams(searchParams);
      navigate({
        pathname: `/home/station/${stationId}/tab`,
        search: newSearchParams.toString() ? `?${newSearchParams.toString()}` : '',
      });
    }
  }, [
    stationStore.isMonitorStationTierForPeriodicReports,
    stationId,
    location.pathname,
    stationStore.currentStationStage,
  ]);

  useEffect(() => {
    const currentStationId = stationStore?.currentStation?.id;

    if (!uuid.validate(stationId)) {
      const newSearchParams = new URLSearchParams(searchParams);

      navigate({
        pathname: `/home/station/${stationStore.stations[0]?.id}`,
        search: newSearchParams.toString() ? `?${newSearchParams.toString()}` : '',
      });
      return;
    }

    // Only update station if it's different from current station
    // This prevents unnecessary updates when only searchParams change (e.g., conversationId)
    if (stationId && stationId !== currentStationId) {
      stationStore.setCurrentStation(stationId);
      // Fetch station stage data to ensure navigation items are properly displayed
      stationStore.fetchStationData(stationId);
    }
    // NOTE: Removed searchParams from dependencies to prevent station switching when only search params change
    // The station should only update when the URL path stationId changes, not when query params change
  }, [stationId]);

  useEffect(() => {
    if (
      windowWidth &&
      ((!stationStore.isSidebarCollapsed && windowWidth <= minResponsiveWidth) ||
        (stationStore.isSidebarCollapsed && windowWidth > minResponsiveWidth))
    )
      stationStore.toggleSidebar();
  }, [windowWidth]);

  function getIsReady() {
    return permissionStore.isReady;
  }

  const renderMainTab = key => (
    <>
      {key !== 'control-center' && <SidePanel tabKey={key} />}
      <Main tabKey={key} />
    </>
  );

  return isReady ? (
    <StationPageContainer $isMobile={isMobile}>
      {isMobile ? <MobileDrawerMenu /> : <SideMenu />}

      <Routes>
        <Route path="rules">
          <Route path="new" element={<RuleCreatePage />} />
          <Route path=":ruleType" element={<RulesPage />} />
          <Route path=":ruleType/:alertRuleId/:alertRecordId/view" element={<RulePage />} />
          <Route path=":ruleType/:id/edit" element={<RuleEditPage />} />
          <Route path="" element={<Navigate replace to="custom" />} />
        </Route>

        <Route path="tab">
          <Route path="map/*" element={renderMainTab('map')} />
          <Route path="trends/*" element={renderMainTab('trends')} />
          <Route path="investigation/*" element={renderMainTab('investigation')} />
          <Route path="control-center/*" element={renderMainTab('control-center')} />
          <Route path="" element={NavigateWithParams('map', true, searchParams)} />
        </Route>

        <Route path="value">
          <Route path="power" element={<ValueDashboardPage tabKey={'power'} />} />
          <Route path="specific-power" element={<ValueDashboardPage tabKey={'specific-power'} />} />
          <Route path="" element={<Navigate replace to="power" />} />
        </Route>

        <Route path="reports" element={<ReportsPage />} />

        <Route path="engine" element={<EnginePage />} />

        <Route path="report-tab" element={<ReportsTabPage />} />

        <Route path="centrifugal" element={<CentrifugalPage />} />
        <Route path="installer" element={<InstallationValidationPage />} />

        <Route path="offering-page" element={<OfferingPage />} />

        <Route path="settings">
          <Route path="users">
            <Route path="" element={<SettingsPage tabKey={'users'} />} />
            <Route path=":userId/edit" element={<UserEditPage />} />
            <Route path="new" element={<UserCreatePage />} />
          </Route>
          <Route
            path="pid-draft"
            element={
              permissionStore.isAuth(permissions.VIEW_STATION_DRAFT) ? (
                <SettingsPage tabKey={'pid-draft'} />
              ) : (
                <Navigate replace to="users" />
              )
            }
          />
          <Route path="" element={<Navigate replace to="users" />} />
        </Route>

        <Route path="lifecycle/*" element={<LifecyclePage />} />

        <Route
          path="ai-assistant"
          element={
            permissionStore.isAuth(permissions.VIEW_SALES_ASSISTANT) ? (
              <AIAssistantPage />
            ) : (
              <Navigate replace to={`/home/station/${stationId}/tab`} />
            )
          }
        />

        <Route path="*" element={NavigateWithParams('tab', true, searchParams)} />
      </Routes>
      {showSiteSurvey && siteSurveyStore?.isUserEligibleToForm && (
        <SiteSurveyOverlay visible={showSiteSurvey} onClose={handleCloseSurvey} salesforceId={salesforceId} />
      )}
    </StationPageContainer>
  ) : (
    <SpinCentered />
  );
});

export default StationPage;
